import type { GeoPoint } from './geo'
/**
 * Partners — organisations we work *with*, as distinct from ones we sell to.
 *
 * Their own record type, decided deliberately. The alternatives were a flag on a
 * lead or a role on a person, and both were wrong for the same reason: a partner
 * that was never a sales prospect has no business in the pipeline, and a partner
 * is usually a company rather than an individual.
 *
 * Smart Elevation is the clearest example — Anna Scheirlinck's company makes
 * snow-safety instruments, distributes through Further Faster, and is a partner
 * rather than a prospect. It currently sits in `skidaCrmLeads` with a flag
 * saying so, which works and is not a model. Moving it is a data decision, not
 * a code one, so it stays where it is until somebody says otherwise.
 *
 * Modelled on the ambassador record on purpose: both are relationships with
 * contact details, a deal, and links maintained elsewhere. Same screen pattern,
 * same editor shape, same shared stylesheet.
 */

export type PartnerStatus = 'active' | 'talking' | 'paused' | 'ended'

export const PARTNER_STATUSES: {
  key: PartnerStatus
  label: string
  color: string
  bg: string
}[] = [
  { key: 'talking', label: 'In conversation', color: '#4c5b82', bg: '#f1f2f3' },
  { key: 'active', label: 'Active', color: '#256820', bg: '#eaf7e7' },
  { key: 'paused', label: 'Paused', color: '#7b5200', bg: '#fff3d8' },
  { key: 'ended', label: 'Ended', color: '#6b7280', bg: '#f1f2f3' },
]

export const partnerStatusStyle = (s: PartnerStatus) =>
  PARTNER_STATUSES.find((x) => x.key === s) ?? PARTNER_STATUSES[0]!

/**
 * What kind of arrangement this is. Free text would drift into forty spellings
 * of "reseller"; an enum would be wrong the first time a deal is unusual. A
 * short closed list plus a description field is the compromise.
 */
export const PARTNER_KINDS = [
  'Reseller',
  'Distributor',
  'Technology',
  'Media',
  'Association',
  'Event',
  'Other',
] as const

export type PartnerKind = (typeof PARTNER_KINDS)[number]

/** Collection: `skidaCrmPartners`. */
export interface Partner {
  /** Where it is, and how precisely — see `GeoPoint`. Written by the geocoder. */
  geo?: GeoPoint
  id: string
  name: string
  kind: PartnerKind
  status: PartnerStatus
  country: string
  website: string
  /**
   * The partner's own logo, on their own URL.
   *
   * Taken from their `og:image` or touch icon rather than copied into our
   * storage: nothing to keep in sync when they rebrand, no third-party logo
   * service told who our partners are, and no licence question about hosting
   * somebody else's mark. The cost is that a dead link is possible, which the
   * table handles by falling back to initials.
   */
  logoUrl: string

  /** Who we actually deal with. One line each — this is not a CRM contact list. */
  contactName: string
  contactRole: string
  email: string
  phone: string

  /**
   * Social handles, stored without the leading @.
   *
   * Here for the same reason ambassadors carry them: when the collector reads
   * who tagged us on Instagram, the platform gives back a username and nothing
   * else. Attribution is a string match against these, so a partner with no
   * handle recorded is a partner whose posts we will see but cannot credit.
   */
  instagram: string
  facebook: string

  /** What the arrangement is, in plain words. The enum only says its shape. */
  arrangement: string
  /** Their tracking code, matching a real code in the product's PromoCodes. */
  campaignCode: string
  /** Free text: when it started, when it renews, who owns it internally. */
  notes: string
  owner: string

  /**
   * Set when the partner also exists as a company in the sales pipeline —
   * Smart Elevation does. Kept as a link rather than a copy so the two records
   * cannot disagree about the company's own details.
   */
  linkedLeadId: string

  createdAt: string | null
  updatedAt: string | null
}

export const EMPTY_PARTNER: Omit<Partner, 'id'> = {
  name: '',
  kind: 'Other',
  status: 'talking',
  country: '',
  website: '',
  logoUrl: '',
  instagram: '',
  facebook: '',
  contactName: '',
  contactRole: '',
  email: '',
  phone: '',
  arrangement: '',
  campaignCode: '',
  notes: '',
  owner: '',
  linkedLeadId: '',
  createdAt: null,
  updatedAt: null,
}
